import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedDetails/components/AssignCardholderDropdown.style'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgSearch from 'src/components/Svgs/SvgSearch'
import TextAvatar from 'src/components/TextAvatar/TextAvatar'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import NewInvitePill from 'src/feature/NewInvitePill/NewInvitePill'
import { useTranslate } from 'src/localization/Trns'
import type { UserListModel } from 'src/models/user.models'
import { useQueryOrgMembers } from 'src/queries/organizations/members.queries'

// Cardholders can only be assigned to active org members — guests and
// bookkeepers are intentionally excluded.
const ASSIGNABLE_ROLES = [
  OrganizationRoles.owner,
  OrganizationRoles.admin,
  OrganizationRoles.cardManager,
  OrganizationRoles.member,
]

const INFINITE_SCROLL_THRESHOLD_PX = 60
const SEARCH_DEBOUNCE_MS = 250
// Mirrors the panel's CSS max-height; the portaled panel never exceeds it.
const PANEL_MAX_HEIGHT = 360
// Smallest below-the-trigger space the panel opens downward into (search row
// + a few options, scrolling internally). Below this the panel flips above
// the trigger as a last resort — downward is preferred because it keeps the
// search box adjacent to the trigger.
const MIN_USABLE_PANEL_HEIGHT = 220
const VIEWPORT_MARGIN = 8

// Whether the panel should open downward from the trigger: prefer down when
// the space below fits a usable panel (or at least beats the space above).
const computeOpenDown = (rect: DOMRect): boolean => {
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
  const spaceAbove = rect.top - VIEWPORT_MARGIN
  return spaceBelow >= MIN_USABLE_PANEL_HEIGHT || spaceBelow >= spaceAbove
}

// Permissive — catches "looks like an email" without trying to fully validate.
// The backend rejects malformed addresses on submit.
const EMAIL_REGEX = /^\S+@\S+\.\S+$/

export type CardholderSelection =
  | { kind: 'member'; member: UserListModel }
  | { kind: 'newContact'; email: string }

interface AssignCardholderDropdownProps {
  /**
   * Fired when the user picks anything. Kept for the card-feed table cell
   * which only needs a trigger, not the selected value. Optional now that
   * `onSelect` covers the controlled use case.
   */
  onAssign?: () => void
  /**
   * Fired with the selected member or new-contact email. When provided,
   * `onAssign` is NOT called — selection-aware callers should use this.
   */
  onSelect?: (selection: CardholderSelection) => void
  /**
   * Controlled selection. When non-null, the trigger renders avatar + label
   * for the selection instead of the default text.
   */
  selected?: CardholderSelection | null
  /** Applied to the trigger button — lets a sibling <label htmlFor> target it. */
  triggerId?: string
  /** Overrides the default placeholder text when no selection. */
  placeholder?: string
  /**
   * Renders the trigger as a persistent bordered input box (form-field look)
   * instead of the default borderless trigger. Use in standalone form contexts
   * like a flyout; leave off for inline use in a DataGrid cell.
   */
  boxed?: boolean
  /**
   * When true, renders the open panel in a `document.body` portal (positioned
   * with `position: fixed` from the trigger's bounding rect) so it isn't
   * clipped by an ancestor with `overflow: hidden` — e.g. a DataGrid cell.
   * The captured position is recomputed on scroll/resize so the panel tracks
   * the trigger rather than drifting. When false/undefined, the panel renders
   * inline.
   */
  portalToBody?: boolean
}

const AssignCardholderDropdown: React.FC<AssignCardholderDropdownProps> = ({
  onAssign,
  onSelect,
  selected,
  triggerId,
  placeholder,
  boxed,
  portalToBody,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const user = useCurrentUser()
  const orgId = user?.organizationId
  // Per-instance id: the panel is portaled to <body>, so the trigger and its
  // listbox aren't DOM-adjacent. A unique id keeps the trigger's aria-controls
  // pointed at this instance's listbox even if multiple dropdowns are open at
  // once (e.g. opened via keyboard, which doesn't fire the outside-click close).
  const listboxId = useId()

  const [open, setOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const prevOpenRef = useRef(open)
  // Captured trigger rect, used to position the portaled panel with
  // position: fixed. Only meaningful when portalToBody is true.
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null)
  // Flip direction is decided once at open time and held for the panel's
  // lifetime — scroll repositioning recomputes the rect every tick, and
  // re-deriving the direction from it would flip the panel mid-scroll the
  // moment the trigger crosses the space threshold.
  const openDownRef = useRef(true)

  // Return focus to the trigger when the panel closes (Escape, outside-click,
  // or selection) so keyboard users aren't dropped at the top of the document.
  // Skip the initial mount (open starts false) by only acting on a true→false
  // transition.
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      triggerRef.current?.focus()
    }
    prevOpenRef.current = open
  }, [open])

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
    }, SEARCH_DEBOUNCE_MS)
    return (): void => clearTimeout(handle)
  }, [searchInput])

  const { data, fetchNextPage, hasNextPage, isFetching } = useQueryOrgMembers({
    orgId,
    queryParams: {
      search: debouncedSearch || undefined,
      organizationRoles: ASSIGNABLE_ROLES,
    },
    enabled: open,
  })

  // Focus the search input on open; reset search state on close so reopening
  // starts from a clean list. The trigger rect is captured synchronously in the
  // click handler (not here), so the portaled panel is already positioned by the
  // time this runs — preventScroll keeps focusing the field from scrolling the
  // page to it.
  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus({ preventScroll: true })
    } else {
      setSearchInput('')
      setDebouncedSearch('')
      setTriggerRect(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node
      // A portaled panel lives outside containerRef, so a click inside it
      // would otherwise read as "outside". Treat clicks inside EITHER the
      // container or the panel as inside.
      const insideContainer = containerRef.current?.contains(target)
      const insidePanel = panelRef.current?.contains(target)
      if (!insideContainer && !insidePanel) {
        setOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    // The portaled panel is positioned with position: fixed from the trigger's
    // rect, so on scroll/resize recompute that rect to keep the panel glued to
    // the trigger. Do NOT close on scroll: the focus-into-view scroll that the
    // open interaction itself fires (amplified inside the DataGrid's virtual
    // scroller) would otherwise close the panel the instant it opens.
    const handleViewportChange = (): void => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect())
      }
    }
    if (portalToBody) {
      // Capture phase catches scrolls inside nested scroll containers (the grid).
      window.addEventListener('scroll', handleViewportChange, true)
      window.addEventListener('resize', handleViewportChange)
    }
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      if (portalToBody) {
        window.removeEventListener('scroll', handleViewportChange, true)
        window.removeEventListener('resize', handleViewportChange)
      }
    }
  }, [open, portalToBody])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    if (isFetching || !hasNextPage) return
    const target = e.currentTarget
    const distanceFromBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight
    if (distanceFromBottom < INFINITE_SCROLL_THRESHOLD_PX) {
      fetchNextPage()
    }
  }

  const handleSelect = (selection: CardholderSelection): void => {
    setOpen(false)
    analytics.track('card feed details', {
      action: 'assign holder type selected',
      assignment_type: selection.kind === 'member' ? 'existing_user' : 'new_user',
    })
    if (onSelect) {
      onSelect(selection)
      return
    }
    onAssign?.()
  }

  const members = data?.members ?? []

  // Pick the trigger style up front: the boxed (flyout) variant reads as a
  // bordered input box, the default reads as a borderless DataGrid-cell trigger;
  // each has its own open-state border.
  const restTriggerStyle = boxed ? style.triggerBoxed : style.trigger
  const openTriggerStyle = boxed ? style.triggerBoxedOpen : style.triggerOpen
  const triggerStyle = open ? openTriggerStyle : restTriggerStyle

  // Only surface the new-contact row when the input is a plausible email AND
  // no loaded member already owns that exact address — avoids the confusing
  // "existing member + new-contact" duplicate state.
  const trimmedSearch = searchInput.trim()
  const showNewContactRow = useMemo(() => {
    if (!EMAIL_REGEX.test(trimmedSearch)) return false
    const lower = trimmedSearch.toLowerCase()
    return !members.some((m) => m.email.toLowerCase() === lower)
  }, [trimmedSearch, members])

  // When portaling, position the panel with position: fixed from the captured
  // trigger rect; otherwise the panel keeps its inline absolute positioning.
  // The flip direction was anchored at open time (openDownRef); only the
  // height re-clamps to the available space as the trigger moves on scroll.
  let portalPanelStyle: React.CSSProperties | undefined
  if (portalToBody && triggerRect) {
    const spaceBelow = window.innerHeight - triggerRect.bottom - VIEWPORT_MARGIN
    const spaceAbove = triggerRect.top - VIEWPORT_MARGIN
    const openDown = openDownRef.current
    portalPanelStyle = {
      position: 'fixed',
      left: triggerRect.left,
      // Explicit width (inline) overrides the panel's CSS `width: 100%`,
      // which would otherwise resolve to the full viewport once the panel
      // is portaled to <body> instead of sitting inside the trigger cell.
      width: triggerRect.width,
      zIndex: 1000,
      // The space can go negative when the trigger is scrolled partially
      // off-viewport; clamp so the style never carries an invalid negative
      // max-height.
      maxHeight: Math.max(
        0,
        Math.min(PANEL_MAX_HEIGHT, openDown ? spaceBelow : spaceAbove),
      ),
      // When flipping up, `top: 'auto'` must explicitly cancel the panel's
      // CSS `top: calc(100% + …)` — otherwise top + bottom both apply and
      // the fixed-position panel collapses off-screen.
      ...(openDown
        ? { top: triggerRect.bottom }
        : { top: 'auto', bottom: window.innerHeight - triggerRect.top }),
    }
  }

  const panel = open ? (
    <div
      ref={panelRef}
      css={style.panel}
      style={portalPanelStyle}
      data-testid="card-feed-assign-cardholder-menu"
    >
      <div css={style.searchInputRow}>
        <div css={style.searchInputWrapper}>
          <SvgSearch
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="currentColor"
            css={style.searchIcon}
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchInput}
            onChange={(e): void => setSearchInput(e.target.value)}
            placeholder={translate('Type or select from list')}
            css={style.searchInput}
            data-testid="card-feed-assign-cardholder-search"
            aria-label={translate('Type or select from list')}
          />
        </div>
      </div>
      <div
        id={listboxId}
        role="listbox"
        aria-label={translate('Cardholders')}
        css={style.listScroll}
        onScroll={handleScroll}
      >
        {showNewContactRow && (
          <>
            <div css={style.groupHeader}>{translate('New Contact')}</div>
            <button
              type="button"
              role="option"
              aria-selected={false}
              css={style.optionRow}
              onClick={(): void =>
                handleSelect({ kind: 'newContact', email: trimmedSearch })
              }
              data-testid="card-feed-assign-cardholder-new-contact"
            >
              <TextAvatar
                text={trimmedSearch[0]?.toUpperCase()}
                size={AvatarSizes.Medium}
              />
              <div css={style.optionTextColumn}>
                <span css={style.optionLabel}>{trimmedSearch}</span>
                <span css={style.newContactCaption}>
                  {translate('Invite new user as Card Manager')}
                </span>
              </div>
            </button>
          </>
        )}
        {members.length > 0 && (
          <div
            css={style.groupHeader}
            data-testid="card-feed-assign-cardholder-members-header"
          >
            {translate('Extend Contacts')}
          </div>
        )}
        {members.map((member) => (
          <button
            key={member.id}
            type="button"
            role="option"
            aria-selected={false}
            css={style.optionRow}
            onClick={(): void => handleSelect({ kind: 'member', member })}
            data-testid="card-feed-assign-cardholder-option"
          >
            <UserAvatar user={member} size={AvatarSizes.Medium} />
            <div css={style.optionTextColumn}>
              <span css={style.optionLabel}>{member.fullNameOrEmail}</span>
              <span css={style.optionSublabel}>{member.email}</span>
            </div>
          </button>
        ))}
        {isFetching && (
          <div css={style.loadingState}>{translate('Loading...')}</div>
        )}
        {!isFetching && !showNewContactRow && members.length === 0 && (
          <div css={style.emptyState}>{translate('No users available')}</div>
        )}
      </div>
    </div>
  ) : null

  return (
    <div css={style.container} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        css={triggerStyle}
        data-testid="card-feed-assign-cardholder"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={(): void => {
          if (open) {
            setOpen(false)
            return
          }
          // Capture the trigger rect synchronously (batched with setOpen) so the
          // portaled panel renders at its final fixed position on the first
          // frame. Otherwise it briefly falls back to its CSS (position:
          // absolute, top:100% of <body>, width:100%), which lurches the page.
          if (portalToBody && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setTriggerRect(rect)
            openDownRef.current = computeOpenDown(rect)
          }
          analytics.track('card feed details', { action: 'assign account holder clicked' })
          setOpen(true)
        }}
      >
        {selected?.kind === 'member' && (
          <span css={style.triggerSelected}>
            <span aria-hidden="true">
              <UserAvatar user={selected.member} size={AvatarSizes.Small} />
            </span>
            <span css={style.triggerSelectedLabel}>
              {selected.member.fullNameOrEmail}
            </span>
          </span>
        )}
        {selected?.kind === 'newContact' && (
          <span css={style.triggerSelected}>
            <span aria-hidden="true">
              <TextAvatar
                text={selected.email[0]?.toUpperCase()}
                size={AvatarSizes.Small}
              />
            </span>
            <span css={style.triggerSelectedLabel}>{selected.email}</span>
          </span>
        )}
        {/*
          Surface the "New Invite" pill in the boxed (flyout) variant when the
          selection is a brand-new contact, mirroring the confirm-assignment
          modal so the invite state is clear before the user submits.
        */}
        {boxed && selected?.kind === 'newContact' && (
          <span css={style.triggerPillSlot}>
            <NewInvitePill inline />
          </span>
        )}
        {!selected && (
          <span css={boxed ? style.triggerPlaceholder : undefined}>
            {placeholder ?? translate('Assign Account Owner')}
          </span>
        )}
        <SvgChevronDown
          width={16}
          height={16}
          viewBox="0 0 24 24"
          css={open ? style.chevronOpen : style.chevron}
        />
      </button>
      {portalToBody
        ? triggerRect && panel && ReactDOM.createPortal(panel, document.body)
        : panel}
    </div>
  )
}

export default AssignCardholderDropdown
